/**
 * ContextRing
 *
 * Circular progress indicator showing session context usage.
 * Green when low, yellow approaching threshold, red near rollover.
 * Shows a small "summary active" icon when summarization is in use.
 */

import { useState } from 'react';
import { FileText } from 'lucide-react';

/**
 * @param {Object} props
 * @param {Object} props.contextUsage - { total_messages, window_max, summary_active, context_fullness_pct, threshold_warning }
 */
export function ContextRing({ contextUsage }) {
  const [showTooltip, setShowTooltip] = useState(false);

  if (!contextUsage) return null;

  const { total_messages = 0, context_fullness_pct = 0, summary_active = false } = contextUsage;
  const maxMessages = 80; // ROLLOVER_THRESHOLD from backend

  // Color based on fullness
  let strokeColor = '#4ade80'; // green
  if (context_fullness_pct >= 80) strokeColor = '#ef4444'; // red
  else if (context_fullness_pct >= 50) strokeColor = '#facc15'; // yellow

  // SVG circle math
  const size = 22;
  const strokeWidth = 2.5;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(context_fullness_pct / 100, 1);
  const dashOffset = circumference * (1 - progress);

  return (
    <div
      className="relative flex items-center gap-1"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Summary active icon */}
      {summary_active && (
        <FileText className="w-3 h-3 text-yellow-500/70" title="Summary active" />
      )}

      {/* Ring */}
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth={strokeWidth}
        />
        {/* Progress arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.5s ease, stroke 0.3s ease' }}
        />
      </svg>

      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute top-full right-0 mt-1.5 w-48 bg-[#1e2028] border border-gray-700/50 rounded-lg shadow-lg p-2.5 z-50 text-label">
          <div className="text-gray-300 font-medium mb-1.5">Context Usage</div>
          <div className="space-y-1 text-gray-400">
            <div className="flex justify-between">
              <span>Messages</span>
              <span className="text-gray-300">{total_messages} / {maxMessages}</span>
            </div>
            <div className="flex justify-between">
              <span>Fullness</span>
              <span style={{ color: strokeColor }}>{Math.round(context_fullness_pct)}%</span>
            </div>
            <div className="flex justify-between">
              <span>Summary</span>
              <span className={summary_active ? 'text-yellow-400' : 'text-gray-500'}>
                {summary_active ? 'Active' : 'Off'}
              </span>
            </div>
          </div>
          {context_fullness_pct >= 80 && (
            <div className="mt-1.5 pt-1.5 border-t border-gray-700/40 text-yellow-400/80 text-label">
              Session nearing limit — will auto-continue soon
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ContextRing;
